import { currentUserId, readSaved, savedKey, writeSaved, SAVED_ENDPOINTS } from "./savedData";
import {
  OFFLINE_WRITE_TEXT,
  POLLED_ENDPOINTS,
  offlineByRule,
  currentPath,
  noteLive,
  noteNetworkFailure,
  noteReachable,
  noteServed,
  noteWriteBlocked,
} from "./offlineState";

// Wraps an RTK Query baseQuery (services/baseQueryWithReauth.js) for the
// admin pages (/rdj-api, Classic included; the public site is untouched):
// - a write (any non GET request) while offline (browser offline, or two
//   polled reads in a row failed, offlineState.offlineByRule) is not sent:
//   it answers { status: "OFFLINE" } with "You are offline, nothing was
//   saved" and the admin shows that toast;
// - a listed read that succeeds overwrites its saved copy (savedData.js);
// - a listed read that fails without an answer (offline, network error, not
//   aborted) answers with the saved copy instead of the error;
// - failures without an answer of the polled reads are counted for the
//   Offline banner and the write rule.
export const isNetworkError = (error) => error?.status === "FETCH_ERROR" || error?.status === "TIMEOUT_ERROR";

const methodOf = (args) => String((typeof args === "object" && args?.method) || "GET").toUpperCase();

export const inAdmin = () => {
  try {
    return /^\/rdj-api(\/|$)/.test(window.location.pathname);
  } catch (e) {
    return false;
  }
};

export function withSavedData(baseQuery, opts = {}) {
  const isAdmin = opts.isAdmin ?? inAdmin;
  const storage = opts.storage;
  const now = opts.now ?? (() => Date.now());
  const pathOf = opts.path ?? currentPath;
  const offline = opts.offline ?? offlineByRule;

  return async (args, api, extraOptions) => {
    if (!isAdmin()) return baseQuery(args, api, extraOptions);
    const method = methodOf(args);
    if (method !== "GET" && offline()) {
      noteWriteBlocked();
      return { error: { status: "OFFLINE", data: { msg: OFFLINE_WRITE_TEXT } } };
    }

    const result = await baseQuery(args, api, extraOptions);
    const aborted = Boolean(api?.signal?.aborted);
    const netFail = isNetworkError(result?.error) && !aborted;
    if (netFail && POLLED_ENDPOINTS.has(api?.endpoint)) noteNetworkFailure();
    else if (!aborted) noteReachable();

    const endpoint = api?.endpoint;
    if (method !== "GET" || api?.type === "mutation" || !SAVED_ENDPOINTS.has(endpoint)) return result;
    const userId = currentUserId(storage);
    if (!userId) return result;
    const key = savedKey(userId, endpoint, args);

    if (!result?.error && result?.data !== undefined) {
      const savedAt = writeSaved({ storage, userId, endpoint, args, data: result.data, now: now() });
      if (savedAt) noteLive(key, pathOf(), savedAt);
      return result;
    }
    if (netFail || (result?.error && !aborted && offline() && typeof result.error.status !== "number")) {
      const saved = readSaved({ storage, userId, endpoint, args });
      if (saved) {
        noteServed(key, pathOf(), saved.savedAt);
        return { data: saved.data, meta: { ...(result?.meta ?? {}), savedAt: saved.savedAt } };
      }
    }
    return result;
  };
}
